import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import buffer from '@turf/buffer';
import { point } from '@turf/helpers';
import { Polygon, MultiPolygon } from 'geojson';
import MapboxFeature from './MapboxFeature';

/**
 * Checks if a location point is within the site boundary plus a buffer zone.
 * @param {number} lat - Latitude of the location point
 * @param {number} lng - Longitude of the location point
 * @param {string | null | undefined} boundaryWkt - WKT string representation of the site boundary
 * @param {number} bufferKm - Buffer distance in kilometers (default: 50)
 * @returns {boolean} - True if the point is within the boundary + buffer, false otherwise
 */
export const isLocationWithinSiteBoundary = (
  lat: number,
  lng: number,
  boundaryWkt: string | null | undefined,
  bufferKm = 5
): boolean => {
  // If no boundary is defined, allow the location (backward compatibility)
  if (!boundaryWkt) {
    return true;
  }

  try {
    // Parse the boundary WKT to GeoJSON
    const boundaryFeature = MapboxFeature.parseFeatureFromString(boundaryWkt);
    
    // Create a point from the location coordinates
    const locationPoint = point([lng, lat]);

    // Check if point is directly within the boundary
    const isWithinBoundary = booleanPointInPolygon(
      locationPoint,
      boundaryFeature.geometry as Polygon | MultiPolygon
    );

    if (isWithinBoundary) {
      return true;
    }

    // Convert buffer from km to meters
    const bufferMeters = bufferKm * 1000;
    
    // Create a buffered version of the boundary
    const bufferedBoundary = buffer(
      boundaryFeature.geometry as Polygon | MultiPolygon,
      bufferMeters,
      { units: 'meters' }
    );

    if (!bufferedBoundary) {
      console.error('Failed to create buffered boundary');
      return false;
    }

    // Check if point is within the buffered boundary
    const isWithinBuffer = booleanPointInPolygon(
      locationPoint,
      bufferedBoundary.geometry as Polygon | MultiPolygon
    );

    return isWithinBuffer;
  } catch (error) {
    console.error('Error validating location against site boundary:', error);
    return true;
  }
};

